/**
 * 03/07 — Merchandising interactif : planogramme (facing) et parcours client.
 * Helpers purs : scores 0-100, effets (multiplicateurs par famille, % trafic /
 * conversion / panier) et conseils pédagogiques.
 */
import data from '../data/dgs/merchandising.json';
import { getSecteur } from './retail';
import { coefType } from './ameliorations';

export const NIVEAUX = data.niveaux;
export const PARCOURS = data.parcours;
export const TYPES_ZONE = data.types_zone;
export const MAX_PROMO_FACING = data.max_promo_facing || 2;
export const MULT_SANS_FACING = 0.8;
export const MULT_HORS_PARCOURS = 0.85;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const r1 = (v) => Math.round(v * 10) / 10;
const r3 = (v) => Math.round(v * 1000) / 1000;

export const nbColonnes = (typeId) => data.colonnes_par_type[typeId] || data.colonnes_par_type.boutique;
export const coutFacing = (typeId) => Math.round(data.cout_reagencement.facing * coefType(typeId));
export const coutParcours = (typeId) => Math.round(data.cout_reagencement.parcours * coefType(typeId));
export const slotKey = (niveauId, col) => `${niveauId}:${col}`;

export const famillesActives = (retail, secteurId = retail?.secteur) => {
  const sec = getSecteur(secteurId);
  if (!sec) return [];
  if (secteurId !== retail?.secteur) return sec.familles;
  return sec.familles.filter(f => retail?.assortiment?.[f.id]?.actif !== false);
};

/** Part de demande saisonnalisée d'une famille pour un mois (1-12). */
export const partSaison = (f, month) => f.part_demande * (month ? (f.saisonnalite?.[((month - 1) % 12 + 12) % 12] ?? 1) : 1);

/** Secteurs présents dans le réseau (principal + magasins ouverts). */
export const secteursReseau = (retail) => {
  if (!retail) return [];
  const ids = [retail.secteur, ...(retail.magasins || []).map(m => m.secteurId || retail.secteur)];
  return [...new Set(ids.filter(Boolean))];
};

/** Config merchandising d'un secteur (migration : ancien format { facing, parcours } = secteur principal). */
export const merchSecteur = (retail, secteurId = retail?.secteur) => {
  const m = retail?.merchandising;
  if (!m) return { facing: null, parcours: null };
  if ('facing' in m || 'parcours' in m) return secteurId === retail.secteur ? { facing: m.facing || null, parcours: m.parcours || null } : { facing: null, parcours: null };
  return m[secteurId] || { facing: null, parcours: null };
};

/** Type de magasin de référence d'un secteur (coef de coût, colonnes du rayon). */
export const typeMagasinSecteur = (retail, secteurId = retail?.secteur) => {
  if (secteurId === retail?.secteur) return retail?.magasin?.typeId;
  return (retail?.magasins || []).find(m => (m.secteurId || retail.secteur) === secteurId)?.typeId || 'boutique';
};

export const getAffinites = (secteurId) => data.affinites[secteurId] || [];

const NEUTRE_FACING = { configure: false, score: null, familleMult: {}, conversionPct: 0, panierPct: 0, conseils: [], details: [], vides: 0, promos: 0 };

/* ───────────────────────── Facing / planogramme ───────────────────────── */

export const computeFacing = (retail, slotsOverride, { secteurId = retail?.secteur, month = null } = {}) => {
  const slots = slotsOverride ?? merchSecteur(retail, secteurId).facing?.slots;
  const fams = famillesActives(retail, secteurId);
  if (!slots || !fams.length) return NEUTRE_FACING;
  const cols = nbColonnes(typeMagasinSecteur(retail, secteurId));
  const total = NIVEAUX.length * cols;
  const totalDemande = fams.reduce((a, f) => a + partSaison(f, month), 0) || 1;
  const visib = {};
  const facings = {};
  const yeux = {};
  let vides = 0;
  let promos = 0;
  let visibTotal = 0;
  NIVEAUX.forEach(n => {
    for (let c = 0; c < cols; c++) {
      const v = slots[slotKey(n.id, c)];
      if (!v) { vides++; continue; }
      if (v === 'promo') { promos++; continue; }
      if (!fams.some(f => f.id === v)) continue;
      facings[v] = (facings[v] || 0) + 1;
      visib[v] = (visib[v] || 0) + n.coef;
      visibTotal += n.coef;
      if (n.id === 'yeux') yeux[v] = (yeux[v] || 0) + 1;
    }
  });
  const details = fams.map(f => {
    const demandeShare = partSaison(f, month) / totalDemande;
    const visibShare = visibTotal ? (visib[f.id] || 0) / visibTotal : 0;
    return { famId: f.id, nom: f.nom, coefficient: f.coefficient, facings: facings[f.id] || 0, demandeShare, visibShare, ratio: visibShare / demandeShare };
  });
  const familleMult = {};
  let ecart = 0;
  details.forEach(d => {
    familleMult[d.famId] = d.facings === 0 ? MULT_SANS_FACING : r3(1 + clamp((d.ratio - 1) * 0.15, -0.1, 0.08));
    ecart += Math.min(1, Math.abs(d.ratio - 1));
  });
  ecart /= details.length;
  const coefs = fams.map(f => f.coefficient).sort((a, b) => a - b);
  const coefMedian = coefs[Math.floor(coefs.length / 2)];
  const yeuxTotal = Object.values(yeux).reduce((a, b) => a + b, 0);
  const yeuxMarge = yeuxTotal ? fams.filter(f => f.coefficient >= coefMedian).reduce((a, f) => a + (yeux[f.id] || 0), 0) / yeuxTotal : 0;
  const videPct = vides / total;
  const panierPct = r1(yeuxMarge * 3);
  const conversionPct = r1(Math.min(promos, MAX_PROMO_FACING) * 1 - videPct * 10);
  const score = clamp(Math.round(50 * (1 - ecart) + 25 * yeuxMarge + 25 * (1 - videPct)), 0, 100);

  const conseils = [];
  details.filter(d => d.facings === 0).forEach(d => conseils.push({ tone: 'bad', texte: `${d.nom} n'a aucun facing : la famille n'est pas visible en rayon (ventes −20 %).` }));
  details.filter(d => d.facings > 0 && d.ratio < 0.7).forEach(d => conseils.push({ tone: 'warn', texte: `${d.nom} est sous-exposée (${Math.round(d.visibShare * 100)} % de visibilité pour ${Math.round(d.demandeShare * 100)} % de la demande) : ajoutez des facings ou montez-la d'un niveau.` }));
  details.filter(d => d.ratio > 1.4).forEach(d => conseils.push({ tone: 'warn', texte: `${d.nom} est sur-exposée (${Math.round(d.visibShare * 100)} % de visibilité pour ${Math.round(d.demandeShare * 100)} % de la demande) : du linéaire gaspillé.` }));
  if (yeuxTotal && yeuxMarge < 0.5) conseils.push({ tone: 'warn', texte: `Niveau des yeux : seulement ${Math.round(yeuxMarge * 100)} % de l'étagère reine va à vos familles à forte marge (coefficient ≥ ${coefMedian}).` });
  if (vides > 0) conseils.push({ tone: 'warn', texte: `${vides} emplacement${vides > 1 ? 's' : ''} vide${vides > 1 ? 's' : ''} : un linéaire à trous donne une impression de rupture (conversion −${r1(videPct * 10)} pt).` });
  if (promos > MAX_PROMO_FACING) conseils.push({ tone: 'warn', texte: `${promos} facings promo : au-delà de ${MAX_PROMO_FACING}, la remise cannibalise vos ventes à plein prix sans gain supplémentaire.` });
  if (score >= 80) conseils.push({ tone: 'good', texte: 'Planogramme équilibré : visibilité proportionnelle à la demande et marge au niveau des yeux.' });
  return { configure: true, score, familleMult, conversionPct, panierPct, conseils, details, vides, promos, yeuxMarge, month };
};

/** Suggestion : facings ∝ demande, familles à forte marge au niveau des yeux. */
export const suggererFacing = (retail, { secteurId = retail?.secteur, month = null } = {}) => {
  const fams = famillesActives(retail, secteurId);
  const cols = nbColonnes(typeMagasinSecteur(retail, secteurId));
  const total = NIVEAUX.length * cols;
  if (!fams.length) return {};
  const totalDemande = fams.reduce((a, f) => a + partSaison(f, month), 0) || 1;
  const alloc = fams.map(f => ({ id: f.id, coefficient: f.coefficient, exact: (partSaison(f, month) / totalDemande) * total }));
  alloc.forEach(a => { a.n = Math.max(1, Math.floor(a.exact)); a.reste = a.exact - Math.floor(a.exact); });
  let restant = total - alloc.reduce((a, x) => a + x.n, 0);
  alloc.sort((a, b) => b.reste - a.reste);
  for (let i = 0; restant > 0; i = (i + 1) % alloc.length) { alloc[i].n++; restant--; }
  alloc.sort((a, b) => b.coefficient - a.coefficient);
  const queue = alloc.flatMap(a => Array(a.n).fill(a.id));
  const slots = {};
  data.ordre_remplissage.forEach(nivId => {
    for (let c = 0; c < cols; c++) slots[slotKey(nivId, c)] = queue.shift() || null;
  });
  return slots;
};

/* ───────────────────────── Parcours client ───────────────────────── */

const { lignes: L, colonnes: C, entree, caisses } = PARCOURS;
export const cellIndex = (l, c) => l * C + c;
export const isCelluleFixe = (i) => i === cellIndex(entree.l, entree.c) || i === cellIndex(caisses.l, caisses.c);
export const typeCelluleFixe = (i) => (i === cellIndex(entree.l, entree.c) ? 'entree' : i === cellIndex(caisses.l, caisses.c) ? 'caisses' : null);
export const voisins = (i) => {
  const l = Math.floor(i / C);
  const c = i % C;
  const out = [];
  if (l > 0) out.push(cellIndex(l - 1, c));
  if (l < L - 1) out.push(cellIndex(l + 1, c));
  if (c > 0) out.push(cellIndex(l, c - 1));
  if (c < C - 1) out.push(cellIndex(l, c + 1));
  return out;
};
export const chaleurBase = (i) => PARCOURS.chaleur[Math.floor(i / C)][i % C];
export const cellulesVides = () => Array.from({ length: L * C }, () => null);

const NEUTRE_PARCOURS = { configure: false, score: null, familleMult: {}, traficPct: 0, conversionPct: 0, panierPct: 0, satisfactionJour: 0, conseils: [], chaleur: PARCOURS.chaleur.flat(), bestId: null };

export const computeParcours = (retail, cellsOverride, { secteurId = retail?.secteur, month = null } = {}) => {
  const cells = cellsOverride ?? merchSecteur(retail, secteurId).parcours?.cells;
  const fams = famillesActives(retail, secteurId);
  if (!cells || !fams.length) return NEUTRE_PARCOURS;
  const best = fams.reduce((a, f) => (partSaison(f, month) > partSaison(a, month) ? f : a), fams[0]);
  const chaleur = PARCOURS.chaleur.flat().slice();
  const indexFam = {};
  const parType = { promo: [], impulsion: [], service: [] };
  let vides = 0;
  cells.forEach((cell, i) => {
    if (isCelluleFixe(i)) return;
    if (!cell) { vides++; return; }
    if (cell.type === 'famille' && fams.some(f => f.id === cell.famId)) indexFam[cell.famId] = [...(indexFam[cell.famId] || []), i];
    else if (parType[cell.type]) parType[cell.type].push(i);
  });
  const conseils = [];
  let score = 40;
  let traficPct = 0;
  let conversionPct = 0;
  let panierPct = 0;
  let satisfactionJour = 0;

  const bestCells = indexFam[best.id] || [];
  const bestIdx = bestCells.length ? bestCells.reduce((a, i) => (chaleurBase(i) > chaleurBase(a) ? i : a), bestCells[0]) : null;
  if (bestIdx != null) {
    const h = chaleurBase(bestIdx);
    if (h <= 0.5) {
      score += 25; conversionPct += 3; traficPct += 1;
      chaleur.forEach((_, i) => { chaleur[i] = Math.min(1, chaleur[i] + 0.1); });
      voisins(bestIdx).forEach(j => { chaleur[j] = Math.min(1, chaleur[j] + 0.15); });
      conseils.push({ tone: 'good', texte: `${best.nom} (votre produit d'appel) est au fond : les clients traversent tout le magasin et réchauffent les zones froides.` });
    } else if (h >= 0.85) {
      score += 0; conversionPct -= 2;
      conseils.push({ tone: 'bad', texte: `${best.nom} est votre produit d'appel : placé à l'entrée, le client le prend, paie et repart sans voir le reste (conversion −2 pts).` });
    } else {
      score += 10;
      voisins(bestIdx).forEach(j => { chaleur[j] = Math.min(1, chaleur[j] + 0.1); });
      conseils.push({ tone: 'warn', texte: `${best.nom} est en zone tiède : reculez-la au fond du magasin pour allonger le parcours.` });
    }
  }

  const familleMult = {};
  fams.forEach(f => {
    const idx = indexFam[f.id];
    if (!idx) {
      familleMult[f.id] = MULT_HORS_PARCOURS;
      score -= 5;
      conseils.push({ tone: 'bad', texte: `${f.nom} n'apparaît pas sur le plan : la famille est reléguée en réserve (ventes −15 %).` });
    } else {
      const expo = idx.reduce((a, i) => a + chaleur[i], 0) / idx.length;
      familleMult[f.id] = r3(1 + (expo - 0.65) * 0.12);
    }
  });

  const caissesIdx = cellIndex(caisses.l, caisses.c);
  const entreeIdx = cellIndex(entree.l, entree.c);
  if (parType.impulsion.length) {
    if (parType.impulsion.some(i => voisins(caissesIdx).includes(i))) {
      score += 10; panierPct += 2;
      conseils.push({ tone: 'good', texte: 'Produits d\'impulsion en file de caisse : la zone la plus chaude du magasin travaille pour le panier (+2 %).' });
    } else {
      score += 5; panierPct += 0.5;
      conseils.push({ tone: 'warn', texte: 'Vos produits d\'impulsion sont loin des caisses : déplacez-les contre la file d\'attente.' });
    }
  }
  if (parType.promo.length) {
    if (parType.promo.some(i => voisins(entreeIdx).includes(i) || chaleurBase(i) >= 0.9)) {
      score += 10; traficPct += 3; panierPct -= 1;
      conseils.push({ tone: 'good', texte: 'Zone promo à l\'entrée : effet d\'attraction immédiat (+3 % trafic), au prix d\'un peu de marge.' });
    } else {
      score += 5; traficPct += 1;
      conseils.push({ tone: 'warn', texte: 'Une zone promo cachée au fond attire peu : l\'entrée est la place des offres choc.' });
    }
  }
  if (parType.service.length) {
    score += 5; conversionPct += 1; satisfactionJour += 0.05;
    conseils.push({ tone: 'good', texte: 'Comptoir conseil / SAV : le client rassuré achète davantage et revient (+1 pt conversion, satisfaction ↑).' });
  }

  let paires = 0;
  getAffinites(secteurId).forEach(([a, b]) => {
    const ia = indexFam[a] || [];
    const ib = indexFam[b] || [];
    if (ia.some(i => voisins(i).some(j => ib.includes(j)))) paires++;
  });
  if (paires) {
    score += Math.min(10, paires * 5); panierPct += paires * 1.5;
    conseils.push({ tone: 'good', texte: `${paires} association${paires > 1 ? 's' : ''} de familles complémentaires côte à côte : cross-merchandising (+${r1(paires * 1.5)} % panier).` });
  } else {
    conseils.push({ tone: 'warn', texte: 'Aucune famille complémentaire voisine : rapprochez ce qui s\'achète ensemble (cross-merchandising).' });
  }

  if (vides) {
    score -= 5 * vides; conversionPct -= vides;
    conseils.push({ tone: 'warn', texte: `${vides} zone${vides > 1 ? 's' : ''} vide${vides > 1 ? 's' : ''} : de la surface louée qui ne vend rien (−${vides} pt conversion).` });
  }

  return {
    configure: true,
    score: clamp(Math.round(score), 0, 100),
    familleMult,
    traficPct: r1(clamp(traficPct, -3, 4)),
    conversionPct: r1(clamp(conversionPct, -8, 5)),
    panierPct: r1(clamp(panierPct, -2, 6)),
    satisfactionJour,
    conseils,
    chaleur,
    bestId: best.id,
  };
};

/** Suggestion : produit d'appel au fond, impulsion en caisse, promo à l'entrée, affinités voisines. */
export const suggererParcours = (retail, { secteurId = retail?.secteur, month = null } = {}) => {
  const fams = famillesActives(retail, secteurId);
  const cells = cellulesVides();
  if (!fams.length) return cells;
  const libres = cells.map((_, i) => i).filter(i => !isCelluleFixe(i));
  const take = (pred) => { const i = libres.find(pred); if (i != null) libres.splice(libres.indexOf(i), 1); return i; };
  const sorted = [...fams].sort((a, b) => partSaison(b, month) - partSaison(a, month));
  const best = sorted[0];
  const bestIdx = take(i => Math.floor(i / C) === 0 && i % C >= 1 && i % C <= 2);
  if (bestIdx != null) cells[bestIdx] = { type: 'famille', famId: best.id };
  const placed = new Set([best.id]);
  getAffinites(secteurId).forEach(([a, b]) => {
    const anchor = placed.has(a) ? a : placed.has(b) ? b : null;
    const other = anchor === a ? b : a;
    if (!anchor || placed.has(other) || !fams.some(f => f.id === other)) return;
    const ai = cells.findIndex(c => c?.famId === anchor);
    const idx = take(i => voisins(ai).includes(i));
    if (idx != null) { cells[idx] = { type: 'famille', famId: other }; placed.add(other); }
  });
  sorted.filter(f => !placed.has(f.id)).forEach(f => {
    const idx = take(i => Math.floor(i / C) <= 1) ?? take(() => true);
    if (idx != null) { cells[idx] = { type: 'famille', famId: f.id }; placed.add(f.id); }
  });
  const impIdx = take(i => voisins(cellIndex(caisses.l, caisses.c)).includes(i));
  if (impIdx != null) cells[impIdx] = { type: 'impulsion' };
  const promoIdx = take(i => voisins(cellIndex(entree.l, entree.c)).includes(i));
  if (promoIdx != null) cells[promoIdx] = { type: 'promo' };
  const svcIdx = take(() => true);
  if (svcIdx != null) cells[svcIdx] = { type: 'service' };
  // Cases restantes : extension de rayon des familles n° 2 et 3 (jamais le produit d'appel).
  let k = 1;
  while (libres.length && sorted.length > 1) {
    const i = libres.shift();
    cells[i] = { type: 'famille', famId: sorted[1 + (k++ % Math.max(1, sorted.length - 1))].id };
  }
  return cells;
};

/* ───────────────────────── Effets combinés (tick) ───────────────────────── */

export const MALUS_SECTEUR_NON_ORGANISE = 0.97;

export const computeMerchFx = (retail, { secteurId = retail?.secteur, month = null } = {}) => {
  const facing = computeFacing(retail, undefined, { secteurId, month });
  const parcours = computeParcours(retail, undefined, { secteurId, month });
  const familleMult = {};
  famillesActives(retail, secteurId).forEach(f => {
    familleMult[f.id] = r3((facing.familleMult[f.id] ?? 1) * (parcours.familleMult[f.id] ?? 1));
  });
  return {
    facing,
    parcours,
    familleMult,
    traficPct: parcours.traficPct,
    conversionPct: r1(facing.conversionPct + parcours.conversionPct),
    panierPct: r1(facing.panierPct + parcours.panierPct),
    satisfactionJour: parcours.satisfactionJour,
  };
};

/** Bilan débrief / supervision : scores, impact estimé sur le CA (%, €/mois). */
/** Multiplicateur global de ventes d'un secteur (magasins secondaires d'un autre secteur). */
export const multGlobalSecteur = (retail, secteurId, month = null) => {
  const fx = computeMerchFx(retail, { secteurId, month });
  if (!fx.facing.configure && !fx.parcours.configure) return secteurId === retail?.secteur ? 1 : MALUS_SECTEUR_NON_ORGANISE;
  const fams = famillesActives(retail, secteurId);
  const totalDemande = fams.reduce((a, f) => a + partSaison(f, month), 0) || 1;
  const multFamilles = fams.reduce((a, f) => a + (partSaison(f, month) / totalDemande) * (fx.familleMult[f.id] ?? 1), 0);
  return (1 + fx.traficPct / 100) * (1 + fx.conversionPct / 100) * (1 + fx.panierPct / 100) * multFamilles;
};

/** Prévision saisonnière : familles qui montent/descendent le mois prochain et score prévu. */
export const previsionSaison = (retail, secteurId = retail?.secteur, month = 1) => {
  const fams = famillesActives(retail, secteurId);
  const suivant = (month % 12) + 1;
  const variations = fams.map(f => {
    const now = partSaison(f, month);
    const next = partSaison(f, suivant);
    return { famId: f.id, nom: f.nom, pct: now ? Math.round((next / now - 1) * 100) : 0 };
  });
  const montent = variations.filter(v => v.pct >= 10).sort((a, b) => b.pct - a.pct);
  const descendent = variations.filter(v => v.pct <= -10).sort((a, b) => a.pct - b.pct);
  const cfg = merchSecteur(retail, secteurId);
  const scoreActuel = cfg.facing ? computeFacing(retail, undefined, { secteurId, month }).score : null;
  const scorePrevu = cfg.facing ? computeFacing(retail, undefined, { secteurId, month: suivant }).score : null;
  const alerte = montent.length > 0 || descendent.length > 0 || (scoreActuel != null && scorePrevu != null && scorePrevu <= scoreActuel - 5);
  return { month, suivant, montent, descendent, scoreActuel, scorePrevu, alerte };
};

export const bilanMerchandising = (retail, totalDays = 0, month = null) => {
  if (!retail) return null;
  const fx = computeMerchFx(retail, { month });
  const configure = fx.facing.configure || fx.parcours.configure;
  const multGlobal = configure ? multGlobalSecteur(retail, retail.secteur, month) : 1;
  const impactPct = Math.round((multGlobal - 1) * 1000) / 10;
  const caMoyenJour = totalDays > 0 ? (retail.stats?.caCumul || 0) / totalDays : 0;
  const impactEurMois = Math.round(caMoyenJour * 30 * (multGlobal - 1) / multGlobal);
  const cfg = merchSecteur(retail);
  return {
    configure,
    facingScore: fx.facing.score,
    parcoursScore: fx.parcours.score,
    scoreMoyen: configure ? Math.round(((fx.facing.score ?? 0) + (fx.parcours.score ?? 0)) / 2) : null,
    traficPct: fx.traficPct,
    conversionPct: fx.conversionPct,
    panierPct: fx.panierPct,
    impactPct,
    impactEurMois,
    facingJour: cfg.facing?.jour ?? null,
    parcoursJour: cfg.parcours?.jour ?? null,
    secteursNonOrganises: secteursReseau(retail).filter(id => !merchSecteur(retail, id).facing),
  };
};
